import { Fragment, useEffect, useMemo, useState } from "react";

import { Dialog } from "primereact/dialog";

import { useDataTable } from "../../../shared/hooks/DataTable";
import { IamColumn } from "../../../shared/hooks/DataTableBasic";
import { FormatDisplay } from "../../../shared/format";
import { PaymentLogService } from "../../lich-su-truyen-nhan/PaymentLogService";
import {
  formatPaymentAmountVnd,
  formatPaymentDateTime,
  renderInvoicePaymentStatusBadge,
} from "../../lich-su-truyen-nhan/lich-su-thanh-toan/paymentLogDisplay";
import type { DataSourceValue } from "../../lich-su-truyen-nhan/lich-su-thanh-toan/constants";

const PAYMENT_HISTORY_CONFIG: Record<
  DataSourceValue,
  {
    header: string;
    emptyMessage: string;
    buildListParams: (id: string) => Record<string, unknown>;
    buildSubtitle: (item: PaymentHistorySelectedItem) => string;
  }
> = {
  invoice: {
    header: "Lịch sử thanh toán hóa đơn",
    emptyMessage: "Hóa đơn chưa phát sinh giao dịch thanh toán qua QR",
    buildListParams: (id) => ({
      resourceId: id,
      sourceType: ["invoice"],
      sorting: "creationTime",
      sortType: "DESCEND",
    }),
    buildSubtitle: (item) => {
      if (!item.invoiceSerial || !item.invoiceNumber) {
        return "";
      }

      return `Ký hiệu ${item.invoiceSerial}, số ${item.invoiceNumber}${
        item.invoiceDate ? `, ngày ${FormatDisplay.date(item.invoiceDate)}` : ""
      }`;
    },
  },
  fuel_log: {
    header: "Lịch sử thanh toán QR Code",
    emptyMessage: "Giao dịch chưa phát sinh giao dịch thanh toán qua QR",
    buildListParams: (id) => ({
      resourceId: id,
      sourceType: ["fuel_log"],
      sorting: "creationTime",
      sortType: "DESCEND",
    }),
    buildSubtitle: (item) => {
      const parts: string[] = [];

      if (item.deviceCode) {
        parts.push(`Vòi bơm ${item.deviceCode}`);
      }
      if (item.storeCode) {
        parts.push(`cửa hàng ${item.storeCode}`);
      }
      if (item.timeStamp) {
        parts.push(`thời gian ${FormatDisplay.dateTime(item.timeStamp)}`);
      }

      return parts.join(", ");
    },
  },
};

export type PaymentHistorySelectedItem = {
  id?: string;
  invoiceSerial?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  deviceCode?: string;
  storeCode?: string;
  timeStamp?: string;
};

type DialogInvoicePaymentHistoryProps = {
  display: boolean;
  setDisplay: (visible: boolean) => void;
  sourceType?: DataSourceValue;
  selectedItem?: PaymentHistorySelectedItem | null;
};

export const DialogInvoicePaymentHistory = ({
  display,
  setDisplay,
  sourceType = "invoice",
  selectedItem,
}: DialogInvoicePaymentHistoryProps) => {
  const config = PAYMENT_HISTORY_CONFIG[sourceType];
  const [requiredParams, setRequiredParams] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useEffect(() => {
    if (display && selectedItem?.id) {
      setRequiredParams(config.buildListParams(selectedItem.id));
      return;
    }

    setRequiredParams(null);
    setIsEmpty(false);
  }, [config, display, selectedItem?.id]);

  const columns = useMemo((): IamColumn[] => [
      {
        field: "transactionId",
        header: "Mã giao dịch",
        width: 180,
      },
      {
        field: "paidAmount",
        header: "Khoản đã thanh toán",
        width: 170,
        dataType: "custom",
        style: { justifyContent: "end" },
        customCell(rowData: { paidAmount?: number }) {
          return (
            <span className="block text-right w-full">
              {formatPaymentAmountVnd(rowData.paidAmount)}
            </span>
          );
        },
      },
      {
        field: "paymentTime",
        header: "Thời gian thanh toán",
        width: 180,
        dataType: "custom",
        customCell(rowData: { paymentTime?: string }) {
          return formatPaymentDateTime(rowData.paymentTime);
        },
      },
      {
        field: "paymentStatus",
        header: "Trạng thái thanh toán",
        width: 170,
        dataType: "custom",
        style: { justifyContent: "center" },
        customCell(rowData: { paymentStatus?: number }) {
          return renderInvoicePaymentStatusBadge(rowData.paymentStatus);
        },
      },
      {
        field: "paymentContent",
        header: "Nội dung",
        minWidth: 220,
      },
    ], []);

  const { render: renderDataTable } = useDataTable({
    columns,
    rows: 20,
    heightCalc: 220,
    hideRowsPerPageDropdown: true,
    hideCurrentPageReport: false,
    requiredParams,
    getList: (params) =>
      PaymentLogService.getList(params).then((data) => {
        setIsEmpty((data?.totalCount ?? 0) === 0);
        return data;
      }),
  });

  const subtitle = selectedItem ? config.buildSubtitle(selectedItem) : "";

  return (
    <Dialog
      header={<span className="text-xl font-bold">{config.header}</span>}
      visible={display}
      style={{ width: "80vw", maxWidth: "1200px" }}
      position="top"
      draggable={false}
      onHide={() => setDisplay(false)}
      className="dialog-invoice-payment-history"
    >
      <Fragment>
        {subtitle ? (
          <p className="text-center m-0 mb-3">
            <b style={{ fontSize: "1.25rem", fontWeight: 700 }}>{subtitle}</b>
          </p>
        ) : null}
        {isEmpty && requiredParams ? (
          <p className="text-center text-color-secondary m-0 mb-3">
            {config.emptyMessage}
          </p>
        ) : null}
        <div className="dialog-invoice-payment-history-table">
          {renderDataTable()}
        </div>
      </Fragment>
    </Dialog>
  );
};
